// src/pages/Tabeeb/TabeebReporting.jsx

import React, { useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import MainLayout from "../../layouts/MainLayout";
import PageLoader from "../../components/Loader/PageLoader";
import ButtonLoader from "../../components/Loader/ButtonLoader";
import { BASE_URL } from "../Config";

const API_BASE = BASE_URL.replace(/\/$/, "");

const PAYMENT_COLUMNS = [
  "id",
  "api_log_id",
  "order_id",
  "store_id",
  "transaction_amount",
  "transaction_type",
  "mobile_account_no",
  "service_number",
  "service_id",
  "email_address",
  "token_number",
  "transaction_date_time",
  "response_code",
  "response_desc",
  "created_at",
  "updated_at",
  "deleted_at",
];

/** Exact headers from EP data.xlsx "Mini App payment" section */
const MINI_APP_HEADERS = [
  "cellno",
  "sub date",
  "service id",
  "amound paid",
  "subscribe mode",
];

const ONETIME_TAB = {
  key: "easypaisa",
  label: "EasyPaisa Payments",
  emptyMessage: "No EasyPaisa payments found.",
};

const LAB_TAB = {
  key: "lab",
  label: "Patient Lab Test Details",
  listEndpoint: "GetPatientLabTestDetails",
  dataKey: "data",
  emptyMessage: "No lab test detail records found.",
};

const REPORT_TABS = [ONETIME_TAB, LAB_TAB];

function getLast10Days() {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - 10);
  const fmt = (d) => d.toISOString().split("T")[0];
  return { from: fmt(from), to: fmt(to) };
}

const { from: defaultFrom, to: defaultTo } = getLast10Days();

function InlineAlert({ type, message, onClose }) {
  if (!message) return null;
  const icon =
    type === "danger"  ? "bi-x-circle-fill" :
    type === "warning" ? "bi-exclamation-triangle-fill" :
                         "bi-info-circle-fill";
  return (
    <div className={`alert alert-${type} alert-dismissible d-flex align-items-center gap-2 py-2`} role="alert">
      <i className={`bi ${icon}`}></i>
      <span>{message}</span>
      <button type="button" className="btn-close ms-auto" onClick={onClose}></button>
    </div>
  );
}

function formatDateTime(value) {
  if (value == null || value === "" || value === "\\N") return "\\N";
  return String(value).replace("T", " ");
}

function formatExportValue(value) {
  if (value == null || value === "" || value === "\\N") return "\\N";
  if (typeof value === "string" && value.includes("T") && !Number.isNaN(Date.parse(value))) {
    return value.replace("T", " ");
  }
  return value;
}

function displayValue(value) {
  if (value == null || value === "" || value === "\\N") return "\\N";
  return String(value);
}

function extractRows(response, preferredKey) {
  if (Array.isArray(response[preferredKey])) return response[preferredKey];
  if (Array.isArray(response.payments)) return response.payments;
  if (Array.isArray(response.chargings)) return response.chargings;
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.records)) return response.records;
  if (Array.isArray(response.labTests)) return response.labTests;
  return [];
}

/** Map recursive charging API → EP data.xlsx Mini App columns */
function mapMiniAppRow(row) {
  return {
    cellno: row.mobileNumber ?? row.mobile_account_no ?? row.cellno ?? "",
    "sub date": formatExportValue(row.createdAt ?? row.created_at ?? row.subscriptionDate ?? ""),
    "service id": row.subscriptionId ?? row.service_id ?? row.serviceId ?? "",
    "amound paid": row.amount ?? row.transaction_amount ?? row.amountPaid ?? "",
    "subscribe mode": row.subscribeMode ?? row.subscribe_mode ?? "EP_MINI",
  };
}

export default function TabeebReporting() {
  const [activeTab, setActiveTab] = useState("easypaisa");
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const [payments, setPayments] = useState([]);
  const [miniAppRows, setMiniAppRows] = useState([]);
  const [paymentPagination, setPaymentPagination] = useState({ totalRecords: 0, totalPages: 1 });
  const [miniPagination, setMiniPagination] = useState({ totalRecords: 0, totalPages: 1, page: 1 });

  const [labData, setLabData] = useState([]);
  const [labColumns, setLabColumns] = useState([]);
  const [labPagination, setLabPagination] = useState({ totalRecords: 0, totalPages: 1 });

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [alert, setAlert] = useState(null);

  function validateDateRange(f = from, t = to) {
    if (!f || !t) {
      setAlert({ type: "warning", message: "Please select both From and To dates." });
      return false;
    }
    if (new Date(f) > new Date(t)) {
      setAlert({ type: "danger", message: "From Date cannot be greater than To Date." });
      return false;
    }
    return true;
  }

  async function fetchPaged(endpoint, preferredKey, p, size = pageSize) {
    const res = await axios.get(`${API_BASE}/${endpoint}`, {
      params: { From: from, To: to, page: p, pageSize: size },
    });
    const response = res.data;
    if (response.responseCode !== "0000") {
      return {
        rows: [],
        message: response.message,
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
      };
    }
    const rows = extractRows(response, preferredKey);
    return {
      rows,
      message: response.message,
      currentPage: response.currentPage || p,
      totalRecords: response.totalRecords ?? rows.length,
      totalPages: response.totalPages || 1,
    };
  }

  async function fetchAllRows(endpoint, preferredKey) {
    const size = 1000;
    let current = 1;
    let allRows = [];
    let pages = 1;
    let lastMessage = null;

    do {
      const res = await axios.get(`${API_BASE}/${endpoint}`, {
        params: { From: from, To: to, page: current, pageSize: size },
      });
      const response = res.data;
      if (response.responseCode !== "0000") {
        return { rows: allRows, message: response.message || lastMessage };
      }
      const rows = extractRows(response, preferredKey);
      allRows = allRows.concat(rows);
      pages = response.totalPages || 1;
      lastMessage = response.message;
      current += 1;
    } while (current <= pages);

    return { rows: allRows, message: lastMessage };
  }

  async function fetchEasyPaisaReport(paymentPage = 1, miniPage = 1) {
    setAlert(null);
    if (!validateDateRange()) return;

    setLoading(true);
    try {
      let oneTime = {
        rows: [],
        message: null,
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
      };
      let recursive = {
        rows: [],
        message: null,
        currentPage: 1,
        totalRecords: 0,
        totalPages: 1,
      };

      try {
        oneTime = await fetchPaged("GetEasyPaisaOnetimePayments", "payments", paymentPage);
      } catch (err) {
        setAlert({
          type: "danger",
          message: err.response?.data?.message || err.response?.data?.title || err.message,
        });
      }

      try {
        recursive = await fetchPaged("GetEasyPaisaRecursiveChargings", "chargings", miniPage);
      } catch {
        // Recursive empty / error is fine — show empty Mini App columns
        recursive = { rows: [], message: null, currentPage: 1, totalRecords: 0, totalPages: 1 };
      }

      setPayments(oneTime.rows);
      setPage(oneTime.currentPage || paymentPage);
      setPaymentPagination({
        totalRecords: oneTime.totalRecords,
        totalPages: oneTime.totalPages || 1,
      });

      setMiniAppRows(recursive.rows.map(mapMiniAppRow));
      setMiniPagination({
        page: recursive.currentPage || miniPage,
        totalRecords: recursive.totalRecords,
        totalPages: recursive.totalPages || 1,
      });

      // Only warn when one-time payments are also empty (recursive empty is OK)
      if (!oneTime.rows.length) {
        setAlert({
          type: "info",
          message: oneTime.message || "No EasyPaisa one-time payments found.",
        });
      }
    } finally {
      setLoading(false);
    }
  }

  async function fetchLabReport(p = 1) {
    setAlert(null);
    if (!validateDateRange()) return;

    setLoading(true);
    try {
      const result = await fetchPaged(LAB_TAB.listEndpoint, LAB_TAB.dataKey, p);
      setLabData(result.rows);
      setLabColumns(result.rows.length ? Object.keys(result.rows[0]) : []);
      setPage(result.currentPage);
      setLabPagination({
        totalRecords: result.totalRecords,
        totalPages: result.totalPages,
      });
      if (!result.rows.length) {
        setAlert({ type: "warning", message: result.message || LAB_TAB.emptyMessage });
      }
    } catch (err) {
      setLabData([]);
      setLabColumns([]);
      setAlert({
        type: "danger",
        message: err.response?.data?.message || err.response?.data?.title || err.message,
      });
    } finally {
      setLoading(false);
    }
  }

  function handleSearch() {
    if (activeTab === "easypaisa") fetchEasyPaisaReport(1, 1);
    else fetchLabReport(1);
  }

  /** Build EP data.xlsx style sheet (matches June / MAY sample exactly) */
  function buildEpStyleSheet(paymentRows, recursiveRows) {
    const aoa = [];
    const colCount = PAYMENT_COLUMNS.length;

    // 1) One-time payments header + rows
    aoa.push([...PAYMENT_COLUMNS]);
    paymentRows.forEach((row) => {
      aoa.push(PAYMENT_COLUMNS.map((col) => formatExportValue(row[col])));
    });

    // 2) Blank gap rows (like June sheet before Mini App title)
    aoa.push(Array(colCount).fill(""));
    aoa.push(Array(colCount).fill(""));
    aoa.push(Array(colCount).fill(""));
    aoa.push(Array(colCount).fill(""));

    // 3) Mini App payment title row (merged A:Q in sample)
    const titleRowIndex = aoa.length;
    const titleRow = Array(colCount).fill("");
    titleRow[0] = "Mini App payment";
    aoa.push(titleRow);

    // 4) Only if recursive data exists → headers (col B+) + data rows (like MAY sheet)
    if (recursiveRows.length > 0) {
      aoa.push(["", ...MINI_APP_HEADERS]);
      recursiveRows.forEach((row) => {
        const mapped = mapMiniAppRow(row);
        aoa.push(["", ...MINI_APP_HEADERS.map((h) => formatExportValue(mapped[h]))]);
      });
    }
    // If no recursive data (June / screenshot) → only title, no mini headers/rows

    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet["!merges"] = [
      { s: { r: titleRowIndex, c: 0 }, e: { r: titleRowIndex, c: colCount - 1 } },
    ];
    return worksheet;
  }

  async function handleTabeebReportDownload() {
    setAlert(null);
    if (!validateDateRange()) return;

    setDownloading(true);
    try {
      if (activeTab === "lab") {
        const { rows, message } = await fetchAllRows(LAB_TAB.listEndpoint, LAB_TAB.dataKey);
        if (!rows.length) {
          setAlert({ type: "warning", message: message || LAB_TAB.emptyMessage });
          return;
        }
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Lab Test Details");
        XLSX.writeFile(workbook, `Tabeeb_Lab_Report_${from}_to_${to}.xlsx`);
        return;
      }

      const oneTime = await fetchAllRows("GetEasyPaisaOnetimePayments", "payments");
      let recursiveRows = [];
      try {
        const recursive = await fetchAllRows("GetEasyPaisaRecursiveChargings", "chargings");
        recursiveRows = recursive.rows;
      } catch {
        recursiveRows = [];
      }

      if (!oneTime.rows.length) {
        setAlert({
          type: "warning",
          message: oneTime.message || "No EasyPaisa one-time payments found.",
        });
        if (!recursiveRows.length) return;
      }

      const worksheet = buildEpStyleSheet(oneTime.rows, recursiveRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "EP data");
      XLSX.writeFile(workbook, `Tabeeb_Report_${from}_to_${to}.xlsx`);
    } catch (err) {
      setAlert({
        type: "danger",
        message: err.response?.data?.message || err.message || "Download failed.",
      });
    } finally {
      setDownloading(false);
    }
  }

  function handleTabChange(tabKey) {
    setActiveTab(tabKey);
    setPayments([]);
    setMiniAppRows([]);
    setLabData([]);
    setLabColumns([]);
    setPage(1);
    setPaymentPagination({ totalRecords: 0, totalPages: 1 });
    setMiniPagination({ totalRecords: 0, totalPages: 1, page: 1 });
    setLabPagination({ totalRecords: 0, totalPages: 1 });
    setAlert(null);
  }

  function handleReset() {
    setFrom(defaultFrom);
    setTo(defaultTo);
    handleTabChange(activeTab);
  }

  function buildPageNumbers(current, total) {
    if (total <= 1) return [];
    const delta = 2;
    const range = [];
    for (let i = Math.max(1, current - delta); i <= Math.min(total, current + delta); i++) {
      range.push(i);
    }
    if (range[0] > 1) {
      range.unshift("...");
      range.unshift(1);
    }
    if (range[range.length - 1] < total) {
      range.push("...");
      range.push(total);
    }
    return range;
  }

  function renderPaymentCell(row, col) {
    const value = row[col];
    if (value == null || value === "" || value === "\\N") return "\\N";
    if (col.includes("date") || col.includes("_at") || col.includes("time")) {
      return formatDateTime(value);
    }
    if (col === "response_desc") {
      const ok =
        String(row.response_code) === "0000" ||
        String(row.response_code) === "0" ||
        String(value).toUpperCase() === "SUCCESS";
      return <span className={`badge ${ok ? "bg-success" : "bg-danger"}`}>{String(value)}</span>;
    }
    if (col === "order_id" || col === "token_number") {
      return <code className="small">{String(value)}</code>;
    }
    return String(value);
  }

  function formatHeader(col) {
    return col.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  function PaginationBar({ current, totalPages, totalRecords, onPage, size = pageSize }) {
    if (!totalRecords) return null;
    const pageNumbers = buildPageNumbers(current, totalPages);
    return (
      <div className="card-footer d-flex flex-wrap justify-content-between align-items-center gap-2 bg-white">
        <small className="text-muted">
          Showing <strong>{(current - 1) * size + 1}</strong>–
          <strong>{Math.min(current * size, totalRecords)}</strong> of{" "}
          <strong>{totalRecords}</strong> records
        </small>
        <nav>
          <ul className="pagination pagination-sm mb-0 gap-1">
            <li className={`page-item ${current === 1 ? "disabled" : ""}`}>
              <button className="page-link rounded" onClick={() => onPage(1)} disabled={current === 1}>
                <i className="bi bi-chevron-double-left"></i>
              </button>
            </li>
            <li className={`page-item ${current === 1 ? "disabled" : ""}`}>
              <button className="page-link rounded" onClick={() => onPage(current - 1)} disabled={current === 1}>
                <i className="bi bi-chevron-left"></i>
              </button>
            </li>
            {pageNumbers.map((p, i) =>
              p === "..." ? (
                <li key={`e-${i}`} className="page-item disabled">
                  <span className="page-link border-0 bg-transparent">…</span>
                </li>
              ) : (
                <li key={p} className={`page-item ${p === current ? "active" : ""}`}>
                  <button className="page-link rounded" onClick={() => onPage(p)}>{p}</button>
                </li>
              )
            )}
            <li className={`page-item ${current >= totalPages ? "disabled" : ""}`}>
              <button className="page-link rounded" onClick={() => onPage(current + 1)} disabled={current >= totalPages}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </li>
            <li className={`page-item ${current >= totalPages ? "disabled" : ""}`}>
              <button className="page-link rounded" onClick={() => onPage(totalPages)} disabled={current >= totalPages}>
                <i className="bi bi-chevron-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  }

  return (
    <MainLayout>
      <div className="container-fluid px-0">

        <div className="page-header-responsive mb-3">
          <h4 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <i className="bi bi-clipboard2-pulse text-primary"></i>
            Tabeeb Reporting
          </h4>
          <button
            className="btn btn-success btn-sm"
            onClick={handleTabeebReportDownload}
            disabled={downloading || loading}
          >
            {downloading
              ? <ButtonLoader label="Downloading…" />
              : <><i className="bi bi-download me-2"></i>Tabeeb Report Download</>}
          </button>
        </div>

        <InlineAlert {...(alert || {})} onClose={() => setAlert(null)} />

        <ul className="nav nav-tabs nav-tabs-scroll mb-3">
          {REPORT_TABS.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                type="button"
                className={`nav-link ${activeTab === tab.key ? "active" : ""}`}
                onClick={() => handleTabChange(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-6 col-md-3">
                <label className="form-label fw-semibold small mb-1">From</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="col-6 col-md-3">
                <label className="form-label fw-semibold small mb-1">To</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={to}
                  min={from || undefined}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className="col-12 col-md-4 d-flex gap-2">
                <button
                  className="btn btn-primary btn-sm flex-grow-1"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm"></span>
                    : <><i className="bi bi-search me-1"></i>Search</>}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={handleReset} title="Reset">
                  <i className="bi bi-arrow-counterclockwise me-1"></i>Clear
                </button>
              </div>
            </div>
          </div>
        </div>

        {activeTab === "easypaisa" ? (
          <>
            {/* ── One-time payments (top section like EP data.xlsx) ── */}
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white fw-semibold">
                EasyPaisa One-time Payments
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <PageLoader label="Loading report…" />
                ) : payments.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No one-time payments found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered mb-0 align-middle small">
                      <thead className="table-light">
                        <tr>
                          <th>#</th>
                          {PAYMENT_COLUMNS.map((col) => (
                            <th key={col}>{formatHeader(col)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((row, idx) => (
                          <tr key={row.id ?? `p-${idx}`}>
                            <td className="text-muted">{(page - 1) * pageSize + idx + 1}</td>
                            {PAYMENT_COLUMNS.map((col) => (
                              <td key={col}>{renderPaymentCell(row, col)}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <PaginationBar
                current={page}
                totalPages={paymentPagination.totalPages}
                totalRecords={paymentPagination.totalRecords}
                onPage={(p) => fetchEasyPaisaReport(p, miniPagination.page)}
              />
            </div>

            {/* ── Mini App payment (same as EP data.xlsx June / MAY) ── */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white border-0 py-3">
                <div className="text-end fw-semibold text-dark">Mini App payment</div>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-4">
                    <span className="spinner-border spinner-border-sm text-primary"></span>
                  </div>
                ) : miniAppRows.length === 0 ? (
                  /* No recursive data → only title (June sheet / screenshot), blank below */
                  <div className="px-3 pb-3 text-muted small" style={{ minHeight: 24 }} />
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-bordered mb-0 align-middle small">
                      <thead className="table-light">
                        <tr>
                          {MINI_APP_HEADERS.map((col) => (
                            <th key={col}>{col}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {miniAppRows.map((row, idx) => (
                          <tr key={`m-${idx}`}>
                            {MINI_APP_HEADERS.map((col) => (
                              <td key={col}>{displayValue(row[col])}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              {miniAppRows.length > 0 && (
                <PaginationBar
                  current={miniPagination.page}
                  totalPages={miniPagination.totalPages}
                  totalRecords={miniPagination.totalRecords}
                  onPage={(p) => fetchEasyPaisaReport(page, p)}
                />
              )}
            </div>
          </>
        ) : (
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              {loading ? (
                <PageLoader label="Loading lab details…" />
              ) : labData.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                  No data found. Select dates and click Search.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-bordered mb-0 align-middle small">
                    <thead className="table-light">
                      <tr>
                        <th>#</th>
                        {labColumns.map((col) => (
                          <th key={col}>{formatHeader(col)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {labData.map((row, idx) => (
                        <tr key={row.id ?? `l-${idx}`}>
                          <td className="text-muted">{(page - 1) * pageSize + idx + 1}</td>
                          {labColumns.map((col) => (
                            <td key={col}>{displayValue(row[col])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <PaginationBar
              current={page}
              totalPages={labPagination.totalPages}
              totalRecords={labPagination.totalRecords}
              onPage={fetchLabReport}
            />
          </div>
        )}
      </div>

      <style>{`
        .page-link { min-width: 32px; text-align: center; }
        .nav-tabs .nav-link {
          color: #495057;
          cursor: pointer;
          border: none;
          border-bottom: 2px solid transparent;
          background: transparent;
        }
        .nav-tabs .nav-link.active {
          color: #50C4CC;
          border-bottom-color: #50C4CC;
          font-weight: 600;
        }
        .nav-tabs .nav-link:hover { color: #50C4CC; }
      `}</style>
    </MainLayout>
  );
}
